import React, { forwardRef, HTMLAttributes, useMemo, useEffect } from 'react';

import { usePrevious } from '@adsk/alloy-react-helpers';
import { ChevronDownIcon } from '@adsk/alloy-react-icon';
import { useTheme } from '@adsk/alloy-react-styles';

const getChevronAnimation = ({ expanded }: { expanded: boolean }) => {
  const transform = expanded ? 'rotate(0deg)' : 'rotate(-90deg)';

  return {
    transform,
    transition: 'transform 0.15s linear',
  };
};

type MenuGroupHeaderProps = HTMLAttributes<HTMLDivElement> & {
  allowCollapsibleGroups?: boolean;
  expanded?: boolean;
  isHighlighted?: boolean;
  scrollToViewVirtual?: () => void;
  isVisibleVirtual?: () => boolean;
};

const MenuGroupHeader = forwardRef<HTMLDivElement, MenuGroupHeaderProps>(
  (
    {
      allowCollapsibleGroups,
      expanded,
      isHighlighted,
      scrollToViewVirtual,
      isVisibleVirtual,
      children,
      ...props
    },
    ref,
  ) => {
    const chevronAnimation = useMemo(
      () => getChevronAnimation({ expanded: !!expanded }),
      [expanded],
    );
    const prevExpanded = usePrevious(expanded);

    useEffect(() => {
      if (!prevExpanded && expanded) {
        if (!isVisibleVirtual || !isVisibleVirtual()) {
          scrollToViewVirtual?.();
        }
      }
    }, [expanded, isVisibleVirtual, prevExpanded, scrollToViewVirtual]);

    const { tokens } = useTheme();
    const groupHeaderTokens = tokens?.component.dropdown?.menu?.groupHeader;

    return (
      <div
        ref={ref}
        {...props}
        dir="auto"
        aria-expanded={allowCollapsibleGroups ? expanded : undefined}
        css={[
          groupHeaderTokens?._,
          allowCollapsibleGroups && groupHeaderTokens?.collapsible?.true,
          isHighlighted && groupHeaderTokens?.highlighted?.true,
          { '&:not(:last-child)': groupHeaderTokens?.divider },
        ]}
        data-testid="menu-group-header"
      >
        {allowCollapsibleGroups && (
          <ChevronDownIcon
            style={{
              ...groupHeaderTokens?.chevron,
              ...chevronAnimation,
            }}
            size={16}
          />
        )}

        {children}
      </div>
    );
  },
);

MenuGroupHeader.displayName = 'MenuGroupHeader';

export default MenuGroupHeader;
